import { Bar, BarChart, CartesianGrid, Cell, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card, ErrorState, Loading, Metric, MetricRow, SectionHeader, Table, fmtMinutes } from '../components.jsx'
import { useApi, useRange } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Failure-mode analysis — the page that turns "here is what broke" into "here is what to change".
 *
 * Ranked by **downtime caused**, never by occurrence count. That is the whole reason `failure_code` carries an
 * ISO-14224-style (component, mode, cause) triple instead of a free-text label: ten trivial limit-switch faults
 * and one gearbox seizure are not the same maintenance problem, and a count-ordered list puts the wrong one at
 * the top of the page.
 *
 * The cumulative line is a genuine Pareto: it answers "how many modes account for 80% of our losses", which is
 * the question that decides where the next shutdown's effort goes.
 */
export default function FailureAnalysis() {
  const { key, setKey, department, setDepartment, query } = useRange('90')

  const modes = useApi(`/rcm/failure-modes${query}`)
  const recurring = useApi(`/analytics/recurring-faults${query}&minCount=2`)

  const data = modes.data || []
  const total = data.reduce((a, m) => a + m.downtimeMinutes, 0)
  const unclassified = data.filter((m) => m.failureCode === null)
  const classified = data.filter((m) => m.failureCode !== null)

  // Cumulative share, in downtime order, so the 80% cut-off is readable off the chart.
  let running = 0
  const pareto = data.slice(0, 12).map((m) => {
    running += m.downtimeSharePct
    return {
      name: m.failureMode,
      hours: +(m.downtimeMinutes / 60).toFixed(1),
      cumulative: +running.toFixed(1),
      classified: m.failureCode !== null,
    }
  })

  const vitalFew = countToEighty(data)

  return (
    <>
      <PageHeader
        title="Failure analysis"
        subtitle="Which failure modes actually cost the plant its availability"
        range={{ key, setKey, department, setDepartment }}
      />

      {modes.loading && <Loading rows={6} />}
      {modes.error && <ErrorState error={modes.error} onRetry={modes.reload} />}

      {modes.data && (data.length === 0 ? (
        <Card>
          <p className="muted">No failures recorded in this period.</p>
        </Card>
      ) : (
        <>
          <MetricRow>
            <Metric label="Distinct modes" value={classified.length} hint="With a confirmed failure code" />
            <Metric
              label="Modes causing 80%"
              value={vitalFew}
              hint="Fix these first"
              kind="warning"
            />
            <Metric label="Total downtime" value={fmtMinutes(total)} />
            <Metric
              label="Unclassified"
              value={`${unclassified.reduce((a, m) => a + m.downtimeSharePct, 0).toFixed(0)}%`}
              hint="Of downtime, with no cause recorded"
              kind={unclassified.reduce((a, m) => a + m.downtimeSharePct, 0) > 20 ? 'danger' : undefined}
            />
          </MetricRow>

          <SectionHeader hint="Bars are downtime; the line is the running share of all downtime">
            Pareto — where the losses are
          </SectionHeader>
          <Card>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={pareto} margin={{ top: 8, right: 8, bottom: 60, left: 0 }}>
                <CartesianGrid stroke="var(--outline-variant)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  stroke="var(--on-surface-variant)"
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={70}
                />
                <YAxis yAxisId="h" tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" unit="h" width={48} />
                <YAxis yAxisId="c" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" unit="%" width={44} />
                <Tooltip content={<Tip />} />
                <Bar yAxisId="h" dataKey="hours" name="Downtime (h)" radius={[3, 3, 0, 0]}>
                  {pareto.map((p, i) => (
                    <Cell key={i} fill={p.classified ? 'var(--sail-blue)' : 'var(--steel)'} />
                  ))}
                </Bar>
                <Line yAxisId="c" type="monotone" dataKey="cumulative" name="Cumulative (%)" stroke="var(--warning)" strokeWidth={2} dot={{ r: 2 }} />
              </BarChart>
            </ResponsiveContainer>
            {unclassified.length > 0 && (
              <p className="muted">
                Bars in steel grey are downtime with <strong>no confirmed failure code</strong>. They are
                included rather than dropped: a Pareto that quietly excluded them would look authoritative while
                describing only part of the plant&apos;s losses. Engineers selecting a code when resolving is what
                moves this into a named cause.
              </p>
            )}
          </Card>

          <SectionHeader>Every mode, with its cost</SectionHeader>
          <Card>
            <Table
              keyOf={(m) => `${m.failureCode || 'none'}-${m.failureMode}`}
              columns={[
                {
                  key: 'mode',
                  header: 'Failure mode',
                  render: (m) => (
                    <span className="cell-stack">
                      <strong>{m.failureMode}</strong>
                      {m.cause && <span className="muted">Cause: {m.cause}</span>}
                    </span>
                  ),
                },
                { key: 'code', header: 'Code', render: (m) => m.failureCode || <span className="muted">—</span> },
                { key: 'component', header: 'Component', render: (m) => m.componentType || <span className="muted">—</span> },
                { key: 'occurrences', header: 'Occurrences', numeric: true },
                { key: 'assets', header: 'Cranes', numeric: true, render: (m) => m.affectedAssets },
                { key: 'downtime', header: 'Downtime', numeric: true, render: (m) => fmtMinutes(m.downtimeMinutes) },
                { key: 'share', header: 'Share', numeric: true, render: (m) => `${m.downtimeSharePct.toFixed(1)}%` },
                { key: 'mttr', header: 'Avg MTTR', numeric: true, render: (m) => fmtMinutes(m.mttrMinutes) ?? '—' },
              ]}
              rows={data}
            />
          </Card>
        </>
      ))}

      <SectionHeader hint="Two or more occurrences of the same fault on one crane">
        Repeat faults
      </SectionHeader>
      <Card>
        {recurring.loading && <Loading rows={3} />}
        {recurring.error && <ErrorState error={recurring.error} onRetry={recurring.reload} />}
        {recurring.data && (
          <>
            <Table
              keyOf={(r) => `${r.assetId}-${r.standardIssueId || r.faultName}`}
              columns={[
                { key: 'assetCode', header: 'Crane', render: (r) => <strong>{r.assetCode}</strong> },
                { key: 'faultName', header: 'Fault', render: (r) => r.faultName || <span className="muted">Unnamed</span> },
                {
                  key: 'groupedBy',
                  header: 'Basis',
                  render: (r) => (r.groupedBy === 'FAILURE_CODE'
                    ? <span title="Grouped on a confirmed failure code">Confirmed cause</span>
                    : <span className="muted" title="Grouped on the operator's reported symptom">Reported symptom</span>),
                },
                { key: 'count', header: 'Occurrences', numeric: true },
              ]}
              rows={recurring.data}
              empty={<p className="muted">No fault has recurred on the same crane in this period.</p>}
            />
            {recurring.data.length > 0 && (
              <p className="muted">
                A repeat means the root cause was not eliminated, so the next occurrence is already paid for.
                &quot;Reported symptom&quot; groupings are weaker evidence than a confirmed cause — two different
                faults can be described the same way by an operator.
              </p>
            )}
          </>
        )}
      </Card>
    </>
  )
}

/** How many modes make up the first 80% of downtime — the "vital few". */
function countToEighty(modes) {
  let acc = 0
  let n = 0
  for (const m of modes) {
    acc += m.downtimeSharePct
    n += 1
    if (acc >= 80) break
  }
  return n
}

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tip">
      <strong>{label}</strong>
      {payload.map((p) => (
        <div key={p.name}><span>{p.name}</span><span className="tnum">{p.value}</span></div>
      ))}
    </div>
  )
}
